// src/components/TermsOfService.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

function TermsOfService() {
  const lastUpdated = 'January 14, 2025';
  const contactEmail = 'support@namecamper.com';

  return (
    <div className="terms-of-service">
      <div className="terms-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: {lastUpdated}</p>

        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using NameCamper ("Service"), you agree to be bound by these
            Terms of Service ("Terms"). If you do not agree to these Terms, do not use
            the Service.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            NameCamper is a monitoring service that checks the availability of usernames
            on social media platforms and domain names. When a monitored name becomes
            available, we notify you via email. We do not register or claim names on
            your behalf.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. Account Registration</h2>
          <p>To use the Service, you must:</p>
          <ul>
            <li>Create an account with accurate and complete information</li>
            <li>Be at least 13 years of age</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized account access</li>
          </ul>
          <p>
            You are responsible for all activities that occur under your account.
          </p>
        </section>

        <section className="terms-section">
          <h2>4. Acceptable Use</h2>
          <p>You agree NOT to use the Service to:</p>
          <ul>
            <li>Monitor names for illegal purposes or to infringe on others' rights</li>
            <li>Engage in cybersquatting, typosquatting, or trademark infringement</li>
            <li>Harass, stalk, or harm other individuals</li>
            <li>Circumvent any access limitations or usage restrictions</li>
            <li>Reverse engineer, decompile, or attempt to extract source code</li>
            <li>Use automated tools to access the Service beyond normal usage</li>
            <li>Resell or redistribute the Service without authorization</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>5. Service Limitations</h2>
          <p>You acknowledge and agree that:</p>
          <ul>
            <li>
              <strong>No Guarantee of Availability:</strong> We cannot guarantee that a
              name will still be available when you attempt to register it. Name
              availability can change at any moment.
            </li>
            <li>
              <strong>Monitoring Frequency:</strong> We check name availability
              periodically, not in real-time. There may be delays between a name
              becoming available and your notification.
            </li>
            <li>
              <strong>Third-Party Platforms:</strong> We have no control over social
              media platforms or domain registrars. Their policies, availability, and
              functionality may change without notice.
            </li>
            <li>
              <strong>Service Availability:</strong> The Service may be temporarily
              unavailable due to maintenance, updates, or circumstances beyond our
              control.
            </li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>6. Beta Service</h2>
          <p>
            NameCamper is currently in beta. During the beta period:
          </p>
          <ul>
            <li>The Service is provided free of charge</li>
            <li>Features may change, be added, or removed without notice</li>
            <li>We may introduce paid tiers in the future</li>
            <li>Beta users may receive special pricing when paid plans launch</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>7. Intellectual Property</h2>
          <p>
            The Service, including its design, features, and content, is owned by
            NameCamper and protected by intellectual property laws. You may not copy,
            modify, or distribute any part of the Service without our written consent.
          </p>
          <p>
            You retain ownership of any content you submit to the Service (such as the
            names you choose to monitor).
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
            ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that the Service will be uninterrupted, error-free,
            secure, or free of viruses or other harmful components.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NAMECAMPER SHALL NOT BE LIABLE FOR
            ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE
            SERVICE.
          </p>
          <p>
            Our total liability for any claims arising from these Terms or your use of
            the Service shall not exceed the amount you paid us in the twelve (12)
            months preceding the claim, or $100, whichever is greater.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless NameCamper, its officers,
            directors, employees, and agents from any claims, damages, losses, or
            expenses (including legal fees) arising from your use of the Service or
            violation of these Terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Termination</h2>
          <p>
            We may suspend or terminate your account at any time, with or without
            cause, and with or without notice. You may also delete your account at any
            time through your profile settings.
          </p>
          <p>
            Upon termination, your right to use the Service ceases immediately. We may
            delete your data in accordance with our Privacy Policy.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Changes to Terms</h2>
          <p>
            We may modify these Terms at any time. We will notify you of material
            changes by posting the updated Terms on the Service and updating the "Last
            updated" date. Your continued use of the Service after changes constitutes
            acceptance of the new Terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws
            of the United States, without regard to conflict of law principles.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Contact Us</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> {contactEmail}
          </p>
        </section>

        <div className="terms-actions">
          <Link to="/" className="btn btn-primary">
            Back to Home
          </Link>
          <Link to="/privacy" className="btn btn-secondary">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
